import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { deepMergeOverrides } from "@/lib/textOverrides";

const dict = {
  fi: {
    nav: {
      services: "Palvelut",
      pricing: "Hinnasto",
      contact: "Yhteystiedot",
      book: "Varaa nyt",
    },
    hero: {
      eyebrow: "★ Helsinki · Laajasalo · 24/7",
      title1: "Pakettiauto kuljettajalla,",
      title2: "paikalla tunnissa.",
      sub: "Varaa muutto kolmella klikkauksella — alkaen",
      from: "alk.",
      ctaCall: "Soita nyt",
      ctaWA: "WhatsApp",
      tag: "Muuttopalvelut · kuljetukset · Helsinki",
    },
    booking: {
      heading: "Varaa pakettiauto",
      sub: "Saat hinta-arvion välittömästi.",
      from: "Lähtöosoite",
      fromPh: "Numero + katu, esim. 12 Mannerheimintie",
      to: "Määränpää",
      toPh: "Numero + katu, esim. 53 Aleksanterinkatu",
      addStop: "+ Lisää välipysähdys",
      day: "Päivämäärä",
      today: "Tänään",
      tomorrow: "Huomenna",
      dayAfter: "Ylihuomenna",
      time: "Kellonaika",
      duration: "Kesto",
      asap: "Heti kun mahdollista",
      service: "Palvelutyyppi",
      seePrice: "Näytä hinta",
      result: "Arvioitu hinta",
      resultSub: "Lopullinen hinta vahvistetaan tilauksen yhteydessä.",
      bookCall: "Vahvista puhelimitse",
      bookWA: "Vahvista WhatsAppissa",
      newQuote: "Uusi laskelma",
      missing: "Täytä lähtö- ja määränpääosoite.",
      distance: "etäisyys",
      pieces: {
        base: "Perusmaksu",
        km: "Kilometrit",
        service: "Palvelu",
        time: "Aika",
      },
    },
    services: {
      heading: "Kolme palvelutyyppiä.",
      sub: "Valitse omasi — hinta päivittyy reaaliajassa.",
      noLabour: {
        t: "Vain kuljetus",
        d: "Pakettiauto + kuljettaja. Sinä lastaat ja purat. Edullisin vaihtoehto.",
        bullet1: "Tilavuus: 11 m³",
        bullet2: "Kuormaus: asiakas",
        bullet3: "Kesto: 30 min lähtien",
        from: "alk. 40 €/h",
      },
      driverHelp: {
        t: "Kuljettaja avustaa kantamista",
        d: "Kuljettaja auttaa lastauksessa ja purkamisessa. Max 30 kg / 1,5 m per esine.",
        bullet1: "Tilavuus: 11 m³",
        bullet2: "Max 30 kg per esine",
        bullet3: "Ihanteellinen sohvalle",
        from: "alk. 64 €/h",
      },
      twoMen: {
        t: "Kuljettaja avustaa kantamista + apulainen",
        d: "2 henkilöä paikalle. Max 90 kg per esine. Ihanteellinen koko muuttoon.",
        bullet1: "Tilavuus: 11 m³",
        bullet2: "Max 90 kg per esine",
        bullet3: "Koko asunnon muutto",
        from: "alk. 99 €/h",
      },
    },
    why: {
      heading: "Miksi Anypaku?",
      sub: "Suoraviivainen, paikallinen, sisukas.",
      items: [
        { t: "Paikalla tunnissa", d: "Helsingin sisällä keskimääräinen vasteaika alle 60 min." },
        { t: "Kiinteä hinta", d: "Hinta lyödään lukkoon ennen lähtöä. Ei piilokuluja." },
        { t: "Vakuutettu", d: "Esineesi vakuutettu kuljetuksen ajan 5 000 €:oon asti." },
        { t: "Suomeksi & englanniksi", d: "Kuljettajat puhuvat sujuvasti molempia kieliä." },
      ],
    },
    map: {
      heading: "Toimipiste Laajasalossa.",
      sub: "Tukikohtamme keskellä Itä-Helsinkiä — palvelemme koko pääkaupunkiseutua.",
      name: "Anypaku Muutto",
      tagline: "anypaku muuttopalvelu · kuljetus",
      address: "Jussaarenkuja 1B, 00840 Helsinki Laajasalo",
      open: "Avoinna ma–su, 24/7",
      itinerary: "Reittiohjeet",
    },
    faq: {
      heading: "Usein kysytyt.",
      items: [
        { q: "Kuinka nopeasti pääsette paikalle?", a: "Helsingin alueella keskimäärin 45–60 minuutin sisällä tilauksesta. Voit myös varata ajan etukäteen." },
        { q: "Mitä maksutapoja hyväksytte?", a: "MobilePay, käteinen, kaikki yleiset pankkikortit. Yritysasiakkaille laskutus." },
        { q: "Mikä on auton tilavuus?", a: "Pakettiautomme tilavuus on 11 m³, ihanteellinen yksiön tai kaksion muuttoon." },
        { q: "Voinko peruuttaa varauksen?", a: "Kyllä, 2 tuntia ennen sovittua aikaa ilmaiseksi." },
      ],
    },
    footer: {
      tag: "Mistä tahansa, minne tahansa.",
      hours: "ma–su · 24/7",
      legal: "© 2026 Anypaku. Y-tunnus 3490541-4",
      madeWith: "Made with sisu in Helsinki",
      switchEN: "EN",
      switchFI: "FI",
    },
  },
  en: {
    nav: {
      services: "Services",
      pricing: "Pricing",
      contact: "Contact",
      book: "Book now",
    },
    hero: {
      eyebrow: "★ Helsinki · Laajasalo · 24/7",
      title1: "Your van with driver,",
      title2: "here within the hour.",
      sub: "Book your move in 3 clicks — from",
      from: "from",
      ctaCall: "Call now",
      ctaWA: "WhatsApp",
      tag: "Moving services · transport · Helsinki",
    },
    booking: {
      heading: "Book a van",
      sub: "Get an instant price estimate.",
      from: "Pickup address",
      fromPh: "Number + street, e.g. 12 Mannerheimintie",
      to: "Drop-off address",
      toPh: "Number + street, e.g. 53 Aleksanterinkatu",
      addStop: "+ Add a stop",
      day: "Date",
      today: "Today",
      tomorrow: "Tomorrow",
      dayAfter: "Day after",
      time: "Time",
      duration: "Duration",
      asap: "As soon as possible",
      service: "Service type",
      seePrice: "Show price",
      result: "Estimated price",
      resultSub: "Final price confirmed at booking.",
      bookCall: "Confirm by phone",
      bookWA: "Confirm on WhatsApp",
      newQuote: "New estimate",
      missing: "Please fill in pickup and drop-off addresses.",
      distance: "distance",
      pieces: {
        base: "Base fee",
        km: "Kilometres",
        service: "Service",
        time: "Time",
      },
    },
    services: {
      heading: "Three service types.",
      sub: "Pick yours — price updates in real time.",
      noLabour: {
        t: "Transport only",
        d: "Van + driver. You load and unload. The most affordable option.",
        bullet1: "Capacity: 11 m³",
        bullet2: "Loading: by you",
        bullet3: "Duration: from 30 min",
        from: "from €40/h",
      },
      driverHelp: {
        t: "Driver assists with carrying",
        d: "The driver helps with loading & unloading. Max 30 kg / 1.5 m per item.",
        bullet1: "Capacity: 11 m³",
        bullet2: "Max 30 kg per item",
        bullet3: "Perfect for a sofa",
        from: "from €64/h",
      },
      twoMen: {
        t: "Driver assists carrying + helper",
        d: "Two-person crew on site. Max 90 kg per item. Ideal for full moves.",
        bullet1: "Capacity: 11 m³",
        bullet2: "Max 90 kg per item",
        bullet3: "Full apartment moves",
        from: "from €99/h",
      },
    },
    why: {
      heading: "Why Anypaku?",
      sub: "Straightforward. Local. Sisu.",
      items: [
        { t: "On site within the hour", d: "Average response time under 60 min within Helsinki." },
        { t: "Fixed price", d: "Price locked before departure. No hidden fees." },
        { t: "Insured", d: "Your items insured up to €5,000 during transport." },
        { t: "Finnish & English", d: "Our drivers speak both fluently." },
      ],
    },
    map: {
      heading: "Hub in Laajasalo.",
      sub: "Our base in East Helsinki — we serve the whole metropolitan area.",
      name: "Anypaku Muutto",
      tagline: "anypaku moving service · transport",
      address: "Jussaarenkuja 1B, 00840 Helsinki Laajasalo",
      open: "Open Mon–Sun, 24/7",
      itinerary: "Get directions",
    },
    faq: {
      heading: "Frequently asked.",
      items: [
        { q: "How quickly can you arrive?", a: "Within Helsinki, on average 45–60 minutes after booking. You can also schedule in advance." },
        { q: "Which payment methods?", a: "MobilePay, cash, all major bank cards. Invoicing for businesses." },
        { q: "What's the van capacity?", a: "11 m³ — ideal for studios and one-bedroom flats." },
        { q: "Can I cancel my booking?", a: "Yes, free cancellation up to 2 hours before the scheduled time." },
      ],
    },
    footer: {
      tag: "From anywhere, to anywhere.",
      hours: "Mon–Sun · 24/7",
      legal: "© 2026 Anypaku. VAT 3490541-4",
      madeWith: "Made with sisu in Helsinki",
      switchEN: "EN",
      switchFI: "FI",
    },
  },
};

const I18nContext = createContext(null);

export const DEFAULT_DICT = dict;

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState("fi");
  const [overrides, setOverrides] = useState({ fi: {}, en: {} });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.getTexts();
        if (!cancelled) {
          setOverrides({
            fi: data?.fi || {},
            en: data?.en || {},
          });
        }
      } catch {
        /* keep empty overrides */
      }
    };
    load();
    const handler = () => load();
    window.addEventListener("anypaku-texts-changed", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("anypaku-texts-changed", handler);
    };
  }, []);

  const value = useMemo(() => {
    const merged = {
      fi: deepMergeOverrides(dict.fi, overrides.fi),
      en: deepMergeOverrides(dict.en, overrides.en),
    };
    return {
      lang,
      setLang,
      t: merged[lang],
      toggle: () => setLang((l) => (l === "fi" ? "en" : "fi")),
    };
  }, [lang, overrides]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
